import {
  ApolloClient,
  ApolloLink,
  from,
  split,
  HttpLink as ApolloHTTPLink,
  InMemoryCache,
  Observable,
  Operation,
  ApolloProvider as QueryProvider,
} from '@apollo/client';
import { BatchHttpLink as ApolloBatchingLink } from '@apollo/client/link/batch-http';
import { ErrorHandler, onError } from '@apollo/client/link/error';
import { RetryLink as ApolloRetryLink } from '@apollo/client/link/retry';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { fetchAuthSession } from '@aws-amplify/auth';
import { createContext, PropsWithChildren, SetStateAction, useContext, useMemo } from 'react';

import { isStr } from '@jebel/utils';

import {
  LOCAL_STORAGE_TOKEN,
  SNACKBAR_ERROR_MESSAGE,
  SNACKBAR_SUCCESS_MESSAGE,
} from 'shared/constants';
import { GRAPHQL_ISSUE_CODE, GraphQLIssue } from 'shared/graphql/errors';
import { useLocalStorageState, useToast } from 'shared/hooks';
import { recordDebug, recordError } from 'shared/utils/record';

const BatchingLink = new ApolloBatchingLink({
  uri: process.env.REACT_APP_ENDPOINT,
  batchMax: 4, // No more than 4 operations per batch
  batchInterval: 50, // Wait no more than 50ms after first batched operation
});

const MutationsLink = new ApolloHTTPLink({
  uri: process.env.REACT_APP_ENDPOINT,
});

const isMutationRequest = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
};

const SubscriptionLink = new WebSocketLink({
  uri: process.env.REACT_APP_8BASE_SUBSCRIPTIONS_ENDPOINT,

  webSocketImpl: class WebSocketProtocol extends WebSocket {
    // Prevents to change the WebSocket protocol.
    // https://docs.8base.com/gettingstarted/commontasks/real-time-applications/#step-3-integrating-apollo-client-and-websocket-support
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(url: string) {
      super(url);
    }
  },

  options: {
    lazy: true,
    reconnect: true,
    reconnectionAttempts: 2,

    connectionParams: () => ({
      // Partially extracted from the 8base documentation
      // https://docs.8base.com/gettingstarted/commontasks/real-time-applications
      workspaceId: process.env.REACT_APP_WORKSPACE_ID,
      environmentName: process.env.REACT_APP_WORKSPACE_ENV,
      token: localStorage.getItem(LOCAL_STORAGE_TOKEN),
    }),
  },
});

const isSubscriptionRequest = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

// Retry link for "Apollo Client".
// Attempt an operation multiple times if it fails due to network or server errors.
// https://www.apollographql.com/docs/react/api/link/apollo-link-retry
const RetryLink = new ApolloRetryLink({
  attempts: {
    max: 2,
  },

  delay: {
    initial: 1000,
    max: 5000,
    jitter: true,
  },
});

const ConnectionLink = split(
  isSubscriptionRequest,
  SubscriptionLink,
  // Handle mutations and queries separately to avoid batching them together having longer response times.
  from([RetryLink, split(isMutationRequest, MutationsLink, BatchingLink)]),
);

const ApolloCache = new InMemoryCache();

export interface ApolloContext {
  /**
   * The authentication token used for GraphQL requests.
   * This token is typically a JWT (JSON Web Token) used to authenticate the user.
   * It can be `undefined` if the user is not authenticated.
   */
  token: string | undefined;

  /**
   * Set the authentication token for the Apollo Client.
   * This is used to authenticate requests to the GraphQL server.
   */
  setToken(action: SetStateAction<string | undefined>): void;
}

const ApolloContext = createContext<ApolloContext>({
  token: undefined,
  setToken: () => {},
});

export function ApolloProvider(props: PropsWithChildren<unknown>) {
  const [token, setToken] = useLocalStorageState<string | undefined>(LOCAL_STORAGE_TOKEN);

  const { showSuccess, showError } = useToast();

  const handleRequestSuccess = ({ getContext }: Operation) => {
    const context = getContext();
    const message = context?.[SNACKBAR_SUCCESS_MESSAGE];

    if (isStr(message)) {
      showSuccess(message);
    }
  };

  const handleRequestError: ErrorHandler = err => {
    recordError(err);

    const issues = err.graphQLErrors ?? [];
    const context = err.operation.getContext();
    const message = context?.[SNACKBAR_ERROR_MESSAGE];

    if (isStr(message)) {
      showError(message);
    }

    const isNotAuthenticated = issues.some((issue: GraphQLIssue) => {
      return (
        issue.code === GRAPHQL_ISSUE_CODE.TokenExpiredErrorCode ||
        issue.code === GRAPHQL_ISSUE_CODE.UserNotFoundErrorCode ||
        issue.code === GRAPHQL_ISSUE_CODE.InvalidTokenErrorCode
      );
    });

    if (isNotAuthenticated) {
      recordDebug('User is not authenticated or token is invalid, fetching new auth session');
      // Attempt to fetch a new authentication session.
      fetchAuthSession({ forceRefresh: true }).catch(recordError);
    }
  };

  const client = useMemo(() => {
    const AuthenticationLink = new ApolloLink((operation, forward) => {
      if (token) {
        operation.setContext({
          headers: {
            // Set the authentication token for the request
            Authorization: `Bearer ${token}`,
          },
        });
      }

      return forward(operation);
    });

    const ResponseHandlerLink = new ApolloLink((operation, forward) => {
      return new Observable(observer => {
        const observable = forward(operation);

        const subscription = observable.subscribe({
          next(response) {
            if (!response.errors) {
              handleRequestSuccess(operation);
            }

            observer.next(response);
          },

          error(err) {
            observer.error(err);
          },

          complete() {
            observer.complete();
          },
        });

        return () => {
          subscription.unsubscribe();
        };
      });
    });

    return new ApolloClient({
      cache: ApolloCache,
      link: from([
        AuthenticationLink,
        onError(handleRequestError),
        ResponseHandlerLink,
        ConnectionLink,
      ]),
    });
  }, [token]);

  return (
    <ApolloContext.Provider value={{ token, setToken }}>
      <QueryProvider client={client}>{props.children}</QueryProvider>
    </ApolloContext.Provider>
  );
}

/**
 * React hook to access the Apollo authentication context.
 *
 * @remarks
 * This hook provides access to the current authentication token and a setter function
 * for updating the token. It must be used within an {@link ApolloProvider}.
 *
 * @throws {Error} Throws if used outside of an ApolloProvider.
 *
 * @returns {ApolloContext} The Apollo authentication context.
 */
export function useApolloContext(): ApolloContext {
  const context = useContext(ApolloContext);

  if (!context) {
    throw new Error('useApolloContext must be used within an ApolloProvider');
  }

  return context;
}
